<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div>
    <Navbar></Navbar>
    <div class="post-page">
      <div v-if="post">
        <el-card class="post-detail-card" shadow="always">
          <template #header>
            <div class="post-header-el">
              <el-avatar :size="40" :src="getProfilePictureUrl(post.User.profile_picture)" />
              <div class="post-meta">
                <span class="username"><UserLink :username="post.User.username" /></span>
                <span class="date">{{ formatDate(post.createdAt) }}</span>
              </div>
            </div>
          </template>

          <p class="post-text">{{ post.content }}</p>
          <el-image
            v-if="post.imageUrl"
            :src="getPostImageUrl(post.imageUrl)"
            fit="cover"
            class="post-image-el"
            :preview-src-list="[getPostImageUrl(post.imageUrl)]"
          />

          <template #footer>
            <div class="post-footer-el">
              <el-tag type="info" size="small">❤️ {{ post.likeCount }}</el-tag>
              <template v-if="sessionUser">
                <el-button size="small" @click="likePost">J'aime</el-button>
                <el-button size="small" @click="removeLike">Je n'aime plus</el-button>
                <el-button
                  v-if="canDelete(post.User && post.User.id)"
                  type="danger"
                  size="small"
                  @click="deletePost"
                >
                  Supprimer
                </el-button>
                <ReportButton
                  v-if="post.User && Number(post.User.id) !== Number(sessionUser.id)"
                  target-type="post"
                  :target-id="post.id"
                />
              </template>
            </div>
          </template>
        </el-card>

        <el-card ref="commentForm" class="comment-form-card" shadow="always">
          <el-input
            ref="commentInput"
            v-model="newCommentText"
            type="textarea"
            :rows="3"
            maxlength="1000"
            show-word-limit
            placeholder="Rédiger un commentaire..."
          />
          <el-button type="primary" class="comment-btn" @click="commentOnPost(post.id, newCommentText)">
            Commenter
          </el-button>
        </el-card>

        <h3 v-if="comments.length > 0" class="comments-title">Commentaires :</h3>

        <el-card v-for="comment in commentThreads" :key="comment.id" class="comment-card" shadow="always">
          <div class="comment-header-el">
            <el-avatar :size="32" :src="getProfilePictureUrl(comment.User && comment.User.profile_picture)" />
            <div class="comment-meta">
              <span class="username"><UserLink :username="comment.User && comment.User.username" /></span>
              <span class="date">{{ formatDate(comment.createdAt) }}</span>
            </div>
          </div>
          <p class="comment-text">{{ comment.text }}</p>
          <div class="comment-actions">
            <el-button v-if="sessionUser" link type="primary" size="small" @click="startReply(comment.id)">
              Répondre
            </el-button>
            <el-button
              v-if="canDelete(comment.User && comment.User.id)"
              link
              type="danger"
              size="small"
              @click="deleteComment(comment.id)"
            >
              Supprimer
            </el-button>
            <ReportButton
              v-if="canReport(comment.User && comment.User.id)"
              target-type="comment"
              :target-id="comment.id"
              link
            />
          </div>

          <!-- Réponses (un seul niveau) -->
          <div v-for="reply in comment.replies" :key="reply.id" class="reply">
            <div class="comment-header-el">
              <el-avatar :size="24" :src="getProfilePictureUrl(reply.User && reply.User.profile_picture)" />
              <div class="comment-meta">
                <span class="username"><UserLink :username="reply.User && reply.User.username" /></span>
                <span class="date">{{ formatDate(reply.createdAt) }}</span>
              </div>
            </div>
            <p class="comment-text">{{ reply.text }}</p>
            <div class="comment-actions">
              <el-button v-if="sessionUser" link type="primary" size="small" @click="startReply(comment.id)">
                Répondre
              </el-button>
              <el-button
                v-if="canDelete(reply.User && reply.User.id)"
                link
                type="danger"
                size="small"
                @click="deleteComment(reply.id)"
              >
                Supprimer
              </el-button>
              <ReportButton
                v-if="canReport(reply.User && reply.User.id)"
                target-type="comment"
                :target-id="reply.id"
                link
              />
            </div>
          </div>

          <div v-if="replyingTo === comment.id" class="reply-form">
            <el-input
              v-model="replyText"
              type="textarea"
              :rows="2"
              maxlength="1000"
              placeholder="Votre réponse..."
            />
            <div class="reply-form-actions">
              <el-button size="small" @click="cancelReply">Annuler</el-button>
              <el-button type="primary" size="small" @click="replyToComment(comment.id)">Répondre</el-button>
            </div>
          </div>
        </el-card>

        <el-card v-if="comments.length === 0" class="no-comments" shadow="never">
          <p>Aucun commentaire pour le moment</p>
        </el-card>
      </div>
    </div>
  </div>
</template>

  <script>
  import api, { API_BASE_URL } from '@/api';
  import Navbar from '@/components/Navbar.vue';
  import UserLink from '@/components/UserLink.vue';
  import ReportButton from '@/components/ReportButton.vue';
  import { notifySuccess, notifyError, confirmAction } from '@/notify';
  import { fetchSessionUser, isStaff } from '@/session';

  export default {
    components: {
      Navbar,
      UserLink,
      ReportButton,
    },
    data() {
      return {
        post: null,
        newCommentText: '',
        comments: [],
        sessionUser: null,
        replyingTo: null,
        replyText: '',
      };
    },

  computed: {
    // Commentaires racines, chacun avec ses réponses (parentId) dans l'ordre chronologique
    commentThreads() {
      const roots = this.comments.filter((c) => !c.parentId);
      return roots.map((root) => ({
        ...root,
        replies: this.comments.filter((c) => Number(c.parentId) === Number(root.id)),
      }));
    },
  },

  mounted() {
    document.body.style.backgroundColor = 'rgba(21, 109, 123)'
  },

  async created() {
    this.sessionUser = await fetchSessionUser()
    await this.fetchPostDetails(this.$route.params.postId)
    this.fetchCommentsForPost(this.$route.params.postId)
    // Arrivée via le bouton « Commenter » d'un fil : on place directement le curseur dans le champ
    if (this.$route.query && this.$route.query.comment) this.focusCommentInput()
  },

    methods: {
      async fetchPostDetails(postId) {
        try {
          const response = await api.get(`/post/getPost/${postId}`);
          this.post = response.data.post;
        } catch (error) {
          console.error('Error fetching post details:', error);
        }
      },
      getProfilePictureUrl(profilePicturePath) {
        return `${API_BASE_URL}/${profilePicturePath}`;
      },
      getPostImageUrl(imagePath) {
        return `${API_BASE_URL}/${imagePath}`;
      },
      async focusCommentInput() {
        await this.$nextTick();
        const form = this.$refs.commentForm;
        if (form && form.$el && form.$el.scrollIntoView) form.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (this.$refs.commentInput) this.$refs.commentInput.focus();
      },
      formatDate(dateString) {
        const options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' };
        return new Date(dateString).toLocaleDateString('fr-FR', options);
      },

      async commentOnPost(postId, commentText) {
      // Un commentaire vide n'est pas envoyé
      if (!commentText || !commentText.trim()) return;
      try {
        const response = await api.post(`/post/${postId}/comment`, {
          text: commentText,
        }, { withCredentials: true });

        if (response.status === 201) {
          this.newCommentText = '';
          notifySuccess('Commentaire publié');
          // On recharge uniquement les commentaires, sans recharger toute la page
          await this.fetchCommentsForPost(postId);
        } else {
          console.error('Error adding comment:', response.data.message);
          notifyError(response.data.message, "Le commentaire n'a pas pu être publié");
        }
      } catch (error) {
        console.error('Error adding comment:', error);
        notifyError(error, "Le commentaire n'a pas pu être publié");
      }
    },

    // Auteur du contenu, ADMIN ou MODERATOR
    // On peut signaler le commentaire d'un autre membre (ou d'un compte supprimé), pas le sien
    canReport(authorId) {
      return Boolean(this.sessionUser) && Number(authorId) !== Number(this.sessionUser.id);
    },

    canDelete(authorId) {
      if (!this.sessionUser) return false;
      return Number(authorId) === Number(this.sessionUser.id) || isStaff(this.sessionUser);
    },

    startReply(commentId) {
      this.replyingTo = commentId;
      this.replyText = '';
    },

    cancelReply() {
      this.replyingTo = null;
      this.replyText = '';
    },

    async replyToComment(parentId) {
      if (!this.replyText || !this.replyText.trim()) return;
      try {
        await api.post(`/post/${this.post.id}/comment`, { text: this.replyText, parentId });
        notifySuccess('Réponse publiée');
        this.cancelReply();
        await this.fetchCommentsForPost(this.post.id);
      } catch (error) {
        notifyError(error, "La réponse n'a pas pu être publiée");
      }
    },

    async deleteComment(commentId) {
      if (!(await confirmAction('Supprimer ce commentaire (et ses réponses) ?'))) return;
      try {
        await api.delete(`/post/${this.post.id}/removeComment/${commentId}`);
        notifySuccess('Commentaire supprimé');
        await this.fetchCommentsForPost(this.post.id);
      } catch (error) {
        notifyError(error, 'Erreur lors de la suppression du commentaire');
      }
    },

    async likePost() {
      try {
        await api.post(`/post/${this.post.id}/like`, {});
        await this.fetchPostDetails(this.post.id);
      } catch (error) {
        notifyError(error, "Impossible d'aimer ce post");
      }
    },

    async removeLike() {
      try {
        await api.delete(`/post/removeLike/${this.post.id}`);
        await this.fetchPostDetails(this.post.id);
      } catch (error) {
        notifyError(error, "Impossible de retirer le j'aime");
      }
    },

    async deletePost() {
      if (!(await confirmAction('Supprimer ce post ?'))) return;
      try {
        await api.delete(`/post/delete/${this.post.id}`);
        notifySuccess('Post supprimé');
        this.$router.back();
      } catch (error) {
        notifyError(error, 'Erreur lors de la suppression');
      }
    },

    async fetchCommentsForPost(postId) {
      try {
        const response = await api.get(`/post/comment/${postId}`)
        this.comments = response.data.comments
      } catch (error) {
        console.error('Error fetching comments:', error)
      }
    },
  },
}
</script>

<style scoped>
.post-page {
  padding: 70px 20px 20px;
  max-width: 700px;
  margin: 0 auto;
}

.post-detail-card,
.comment-form-card {
  margin-bottom: 20px;
}

.post-header-el {
  display: flex;
  align-items: center;
  gap: 12px;
}

.post-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.post-meta .username {
  font-weight: 600;
  color: #303133;
}

.post-meta .date {
  font-size: 12px;
  color: #909399;
}

.post-text {
  margin: 0;
  line-height: 1.5;
  color: #303133;
}

.post-image-el {
  max-height: 300px;
  border-radius: 8px;
  margin-top: 12px;
}

.post-footer-el {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.comment-btn {
  margin-top: 12px;
}

.comments-title {
  color: #fff;
  margin-bottom: 12px;
}

.comment-card {
  margin-bottom: 12px;
}

.comment-header-el {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.comment-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.comment-meta .username {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
}

.comment-meta .date {
  font-size: 11px;
  color: #909399;
}

.comment-text {
  margin: 0;
  color: #606266;
  line-height: 1.4;
}

.comment-actions {
  margin-top: 6px;
}

.reply {
  margin: 12px 0 0 24px;
  padding-left: 12px;
  border-left: 3px solid #e4e7ed;
}

.reply-form {
  margin: 12px 0 0 24px;
}

.reply-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.no-comments {
  margin-top: 12px;
}
</style>
